import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import type { ChatMessage } from '@/api/chat';
import { ChatAttachment } from '@/components/chat/ChatAttachment';
import { chatDayOf } from '@/lib/chatDay';
import { cn } from '@/lib/utils';

/**
 * Пузырь сообщения в чатах сотрудника на телефоне (13.10.2026, docs/CURATOR-MOBILE-DESIGN.md §4.8, §5.24): тот же вид,
 * что у ученика (`MobileBubble`) — своё оранжевое со срезанным правым нижним углом, чужое песочное. Вложения — общим
 * `ChatAttachment` (техдолг 389): фото, видео и PDF внутри пузыря, `javascript:` и битые адреса он не рисует.
 *
 * `aside` — кнопка «⋯» у сообщения в мониторинге админа.
 */
export function StaffBubble({
  msg,
  mine,
  showDay,
  aside,
}: {
  msg: ChatMessage;
  mine: boolean;
  showDay: boolean;
  aside?: ReactNode;
}) {
  const { t, i18n } = useTranslation();
  const time = msg.created_at
    ? new Date(msg.created_at).toLocaleTimeString(i18n.language, { hour: '2-digit', minute: '2-digit' })
    : '';
  const day = msg.created_at
    ? chatDayOf(msg) === new Date().toDateString()
      ? t('chat.today')
      : new Date(msg.created_at).toLocaleDateString(i18n.language, { day: 'numeric', month: 'long' })
    : '';

  return (
    <>
      {showDay && day && (
        <div className="py-1 text-center text-[10.5px] font-semibold uppercase tracking-[0.08em] text-on-surface-variant">
          {day}
        </div>
      )}
      <div data-staff-bubble className={cn('flex max-w-[86%] items-end gap-1', mine ? 'flex-row-reverse self-end' : 'self-start')}>
        <div
          className={cn(
            'min-w-0 px-3.5 py-2.5',
            mine
              ? 'rounded-[22px] rounded-br-lg bg-primary text-white'
              : 'rounded-[22px] rounded-bl-lg bg-surface-container-low text-on-surface',
          )}
        >
          {msg.attachments?.map((a, i) => <ChatAttachment key={a.id ?? `${a.url}-${i}`} attachment={a} />)}
          {msg.content && <div className="whitespace-pre-wrap break-words text-[14px] leading-[1.45]">{msg.content}</div>}
          {time && (
            <div className={cn('mt-1 text-[10px]', mine ? 'text-right text-white/85' : 'text-on-surface-variant')}>{time}</div>
          )}
        </div>
        {aside}
      </div>
    </>
  );
}
